// PolygonAPIManagerV2.js
// New proxy-based Polygon manager. No client-side API key; routes all requests through /proxy-polygon.

class PolygonAPIManagerV2 {
    constructor() {
        this.proxyBaseUrl = '/proxy-polygon';
        // Default fallback TTL for ticker details cache (in minutes) when upstream does not provide one
        this.tickerDetailsCacheExpiration = 5; // 5 minutes fallback
        this.exchangeMap = {
            'XNYS': 'New York Stock Exchange',
            'XASE': 'NYSE American',
            'XNAS': 'NASDAQ',
            'ARCX': 'NYSE Arca',
            'BATS': 'Cboe BZX Exchange',
            'BATY': 'Cboe BYX Exchange',
            'EDGA': 'Cboe EDGA Exchange',
            'EDGX': 'Cboe EDGX Exchange',
            'IEXG': 'Investors Exchange',
            'XCHI': 'Chicago Stock Exchange',
            'XCIS': 'National Stock Exchange',
            'XPHL': 'NASDAQ PSX',
            'N/A': 'Unknown Exchange'
        };
    }

    static async create() {
        if (!PolygonAPIManagerV2.instance) {
            PolygonAPIManagerV2.instance = new PolygonAPIManagerV2();
        }
        return PolygonAPIManagerV2.instance;
    }

    // Helper to construct upstream Polygon URL and wrap with proxy
    buildPolygonUrl(path, params = {}) {
        const upstream = new URL(`https://api.polygon.io${path}`);
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null) upstream.searchParams.set(k, v);
        }
        return `${this.proxyBaseUrl}?url=${encodeURIComponent(upstream.toString())}`;
    }

    // Cache key helper for ticker details
    generateTickerCacheKey(ticker) {
        return `ticker_${ticker}`;
    }

    // Snapshot gainers
    async fetchTopGainersFromSnapshot(limit = 21) {
        try {
            const url = this.buildPolygonUrl('/v2/snapshot/locale/us/markets/stocks/gainers', { limit });
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const jsonData = await response.json();
            return this._mapSnapshot(jsonData, limit, 'asc');
        } catch (error) {
            console.error(`❌ Error fetching snapshot gainer data: ${error.message}`);
            return [];
        }
    }

    // Snapshot losers
    async fetchTopLosersFromSnapshot(limit = 21) {
        try {
            const url = this.buildPolygonUrl('/v2/snapshot/locale/us/markets/stocks/losers', { limit });
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const jsonData = await response.json();
            return this._mapSnapshot(jsonData, limit, 'desc');
        } catch (error) {
            console.error(`❌ Error fetching snapshot loser data: ${error.message}`);
            return [];
        }
    }

    _mapSnapshot(jsonData, limit, sortOrder = 'desc') {
        if (!jsonData || !jsonData.tickers) {
            return [];
        }
        const stocks = jsonData.tickers
            .filter(item => item.ticker && item.todaysChangePerc !== undefined && item.day)
            .map(item => ({
                ticker: item.ticker,
                todaysChangePerc: parseFloat(item.todaysChangePerc || 0),
                day: {
                    c: parseFloat(item.day.c || 0),
                    v: parseInt(item.day.v || 0)
                }
            }))
            .sort((a, b) => sortOrder === 'desc' ? b.todaysChangePerc - a.todaysChangePerc : a.todaysChangePerc - b.todaysChangePerc);

        return sortOrder === 'asc' ? stocks.slice(-limit) : stocks.slice(0, limit);
    }

    // Fetch ticker details (single)
    async fetchTickerDetails(ticker) {
        const url = this.buildPolygonUrl(`/v3/reference/tickers/${encodeURIComponent(ticker)}`);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
    }

    // Fetch splits
    async fetchSplits(ticker, from, to) {
        const url = this.buildPolygonUrl('/v3/reference/splits', {
            ticker,
            'execution_date.gte': from,
            'execution_date.lte': to
        });
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
    }

    // Aggregates range
    async fetchAggsRange(ticker, multiplier, timespan, from, to) {
        const path = `/v2/aggs/ticker/${encodeURIComponent(ticker)}/range/${multiplier}/${timespan}/${from}/${to}`;
        const url = this.buildPolygonUrl(path);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
    }

    // Attempt to derive TTL (in minutes) from upstream response headers. Falls back to this.tickerDetailsCacheExpiration.
    getResponseTtlMinutes(response) {
        try {
            if (!response || !response.headers) return this.tickerDetailsCacheExpiration;
            // Prefer Cache-Control: max-age=SECONDS
            const cacheControl = response.headers.get && response.headers.get('cache-control');
            if (cacheControl) {
                const match = cacheControl.match(/max-age\s*=\s*(\d+)/i);
                if (match && match[1]) {
                    const seconds = parseInt(match[1], 10);
                    if (!isNaN(seconds) && seconds > 0) return Math.ceil(seconds / 60);
                }
            }
            // Fallback to Expires header if present: Expires - Date
            const expires = response.headers.get && response.headers.get('expires');
            const date = response.headers.get && response.headers.get('date');
            if (expires) {
                const expMs = Date.parse(expires);
                const nowMs = date ? Date.parse(date) : Date.now();
                if (!isNaN(expMs) && !isNaN(nowMs)) {
                    const deltaSec = Math.max(0, Math.floor((expMs - nowMs) / 1000));
                    if (deltaSec > 0) return Math.ceil(deltaSec / 60);
                }
            }
        } catch (_) { /* ignore and fallback */ }
        return this.tickerDetailsCacheExpiration;
    }

    // Enrich list of stocks with ticker details (with caching and throttling)
    async enrichWithTickerDetails(stocks, requestsPerSecond = 5) {
        if (!Array.isArray(stocks) || stocks.length === 0) return stocks || [];

        const tickers = stocks.map(stock => stock.ticker);
        const cachedResults = new Map();
        const newTickers = [];

        // Try cache first
        for (const ticker of tickers) {
            const cacheKey = this.generateTickerCacheKey(ticker);
            const cachedData = getCacheItem(cacheKey);
            if (cachedData) {
                cachedResults.set(ticker, { ticker, details: cachedData });
            } else {
                newTickers.push(ticker);
            }
        }

        const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
        const throttleFetch = async (fn, index) => {
            const delayMs = Math.floor(index / requestsPerSecond) * 1000;
            await delay(delayMs);
            return fn();
        };

        if (newTickers.length > 0) {
            const fetchPromises = newTickers.map((ticker, index) => throttleFetch(async () => {
                try {
                    const cacheKey = this.generateTickerCacheKey(ticker);
                    const url = this.buildPolygonUrl(`/v3/reference/tickers/${encodeURIComponent(ticker)}`);
                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`HTTP error for ${ticker}: ${response.status}`);
                    const ttlMinutes = this.getResponseTtlMinutes(response);
                    const data = await response.json();

                    if (data.status !== 'OK' || !data.results) {
                        return { ticker, details: { name: ticker, primary_exchange: 'N/A' } };
                    }

                    const exchangeCode = data.results.primary_exchange || 'N/A';
                    data.results.primary_exchange = this.exchangeMap[exchangeCode] || exchangeCode;

                    let details = data.results;

                    // Derivative/warrant handling
                    const isWarrantByName = ticker.endsWith('W') && /warrant/i.test(details.name || '');
                    const isDerivative = details.type === 'WARRANT' || details.type === 'OPTION' || isWarrantByName;

                    if (isDerivative) {
                        const underlyingTicker = details.underlying || (isWarrantByName ? ticker.slice(0, -1) : ticker.split('.')[0]);
                        if (underlyingTicker) {
                            const underlyingCacheKey = this.generateTickerCacheKey(underlyingTicker);
                            const underlyingCachedData = getCacheItem(underlyingCacheKey);
                            if (underlyingCachedData) {
                                details.underlyingDetails = underlyingCachedData;
                            } else {
                                const underlyingUrl = this.buildPolygonUrl(`/v3/reference/tickers/${encodeURIComponent(underlyingTicker)}`);
                                const underlyingResponse = await fetch(underlyingUrl);
                                if (underlyingResponse.ok) {
                                    const underlyingData = await underlyingResponse.json();
                                    if (underlyingData.status === 'OK' && underlyingData.results) {
                                        underlyingData.results.primary_exchange = this.exchangeMap[underlyingData.results.primary_exchange] || underlyingData.results.primary_exchange;
                                        const underlyingTtl = this.getResponseTtlMinutes(underlyingResponse);
                                        setCacheItem(underlyingCacheKey, underlyingData.results, underlyingTtl);
                                        details.underlyingDetails = underlyingData.results;
                                    }
                                }
                            }
                        }
                    }

                    setCacheItem(cacheKey, details, ttlMinutes);
                    return { ticker, details };
                } catch (error) {
                    console.error(`❌ Error fetching details for ${ticker}: ${error.message}`);
                    const cacheKey = this.generateTickerCacheKey(ticker);
                    const details = { name: ticker, primary_exchange: 'Unknown Exchange' };
                    setCacheItem(cacheKey, details, this.tickerDetailsCacheExpiration);
                    return { ticker, details };
                }
            }, index));

            const fetchedResults = await Promise.all(fetchPromises);
            fetchedResults.forEach(result => cachedResults.set(result.ticker, result));
        }

        const enrichedStocks = stocks.map(stock => ({
            ...stock,
            tickerDetails: cachedResults.get(stock.ticker)?.details || { name: stock.ticker, primary_exchange: 'Unknown Exchange' }
        }));

        return enrichedStocks;
    }

    // 1:1 parity helper: wrap absolute Polygon URL with proxy
    wrapAbsoluteUrlToProxy(url) {
        try {
            if (typeof url === 'string' && url.startsWith('http')) {
                return `${this.proxyBaseUrl}?url=${encodeURIComponent(url)}`;
            }
        } catch (_) { }
        return url;
    }

    // Parity with V1: fetchSnapshotData(url, limit, sortOrder)
    async fetchSnapshotData(url, limit, sortOrder = 'desc') {
        const proxied = (typeof url === 'string' && url.startsWith('http'))
            ? this.wrapAbsoluteUrlToProxy(url)
            : this.buildPolygonUrl(url);
        const response = await fetch(proxied);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const jsonData = await response.json();
        return this._mapSnapshot(jsonData, limit, sortOrder);
    }

    // Parity: fetchTopGainers using grouped aggs + prev close batch
    async fetchTopGainers(limit = 100) {
        try {
            const latestDate = new Date(Date.now() - 86400000).toISOString().split('T')[0];
            const urlPath = `/v2/aggs/grouped/locale/us/market/stocks/${latestDate}`;
            const url = this.buildPolygonUrl(urlPath, { adjusted: 'true' });

            let allStocks = await this.fetchMarketData(url);
            await this.fetchPreviousClosePricesBatch(allStocks);

            const topGainers = allStocks
                .sort((a, b) => b.ChangePercent - a.ChangePercent)
                .slice(0, limit);
            return topGainers;
        } catch (error) {
            console.error(`❌ Error fetching market data: ${error.message}`);
            return [];
        }
    }

    // Parity: fetchMarketData(url)
    async fetchMarketData(url) {
        const proxied = (typeof url === 'string' && url.startsWith('http'))
            ? this.wrapAbsoluteUrlToProxy(url)
            : this.buildPolygonUrl(url);
        const response = await fetch(proxied);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const jsonData = await response.json();
        if (!jsonData.results) {
            console.error("❌ API response is missing 'results' field!");
            return [];
        }
        const stocks = jsonData.results.map(item => ({
            ticker: item.T,
            ChangePercent: 0,
            day: {
                c: parseFloat(item.c || 0),
                v: parseInt(item.v || 0)
            }
        }));
        return stocks;
    }

    // Parity: batch prev close enrichment
    async fetchPreviousClosePricesBatch(stocks) {
        if (!stocks || !stocks.length) return;
        const batchSize = 50;
        const batches = [];
        for (let i = 0; i < stocks.length; i += batchSize) {
            batches.push(stocks.slice(i, i + batchSize));
        }
        const fetchPromises = batches.map(batch => this.fetchPrevCloseForBatch(batch));
        await Promise.all(fetchPromises);
    }

    // Parity: fetchPrevCloseForBatch(batch)
    async fetchPrevCloseForBatch(batch) {
        const tickersQuery = batch.map(s => s.ticker).join(',');
        const url = this.buildPolygonUrl('/v2/aggs/ticker/prev', { tickers: tickersQuery });
        const response = await fetch(url);
        if (!response.ok) {
            console.error(`❌ Error fetching batch previous close prices: ${response.status}`);
            return;
        }
        const jsonData = await response.json();
        if (jsonData.results) {
            jsonData.results.forEach(item => {
                const stock = batch.find(s => s.ticker === item.T);
                if (stock && item.c > 0) {
                    const prevClose = parseFloat(item.c);
                    stock.ChangePercent = ((stock.day.c - prevClose) / prevClose) * 100;
                }
            });
        }
    }

    // Parity: fetchStockData with pagination via proxy
    async fetchStockData(stockSymbol, multiplier, timespan, startDate, endDate) {
        try {
            let currentStartDate = startDate;
            let currentEndDate = endDate;
            let allResults = [];
            let nextUrl = null;
            let pageCount = 0;
            const maxPages = 50;

            const initialPath = `/v2/aggs/ticker/${encodeURIComponent(stockSymbol)}/range/${multiplier}/${timespan}/${currentStartDate}/${currentEndDate}`;
            let currentUrl = this.buildPolygonUrl(initialPath);

            do {
                pageCount++;
                const response = await fetch(currentUrl);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const jsonData = await response.json();

                if (jsonData.results && Array.isArray(jsonData.results) && jsonData.results.length > 0) {
                    allResults = allResults.concat(jsonData.results);
                }

                nextUrl = jsonData.next_url;
                if (nextUrl) {
                    currentUrl = this.wrapAbsoluteUrlToProxy(nextUrl);
                }

                if (pageCount >= maxPages) {
                    console.warn(`⚠️ Reached maximum page limit (${maxPages}), stopping pagination`);
                    break;
                }
            } while (nextUrl && pageCount < maxPages);

            if (allResults.length === 0) {
                const endDateObj = new Date(currentEndDate);
                const isCurrentDateNonTrading = typeof MarketHoursUtility !== 'undefined' && MarketHoursUtility.isMarketHoliday(endDateObj);
                const isMarketCurrentlyOpen = typeof MarketHoursUtility !== 'undefined' && MarketHoursUtility.isMarketOpen();

                if (isCurrentDateNonTrading || !isMarketCurrentlyOpen) {
                    const lastTradingDay = MarketHoursUtility.getLastTradingDay(endDateObj);
                    const lastTradingDayStr = MarketHoursUtility.formatDateForAPI(lastTradingDay);

                    const originalDateDiff = new Date(currentEndDate) - new Date(currentStartDate);
                    const adjustedStartDate = new Date(lastTradingDay.getTime() - originalDateDiff);
                    const adjustedStartDateStr = MarketHoursUtility.formatDateForAPI(adjustedStartDate);

                    const retryPath = `/v2/aggs/ticker/${encodeURIComponent(stockSymbol)}/range/${multiplier}/${timespan}/${adjustedStartDateStr}/${lastTradingDayStr}`;
                    let retryCurrentUrl = this.buildPolygonUrl(retryPath);

                    let retryResults = [];
                    let retryNextUrl = null;
                    let retryPageCount = 0;

                    do {
                        retryPageCount++;
                        const retryResponse = await fetch(retryCurrentUrl);
                        if (!retryResponse.ok) throw new Error(`HTTP error on retry! status: ${retryResponse.status}`);
                        const retryJsonData = await retryResponse.json();

                        if (retryJsonData.results && Array.isArray(retryJsonData.results) && retryJsonData.results.length > 0) {
                            retryResults = retryResults.concat(retryJsonData.results);
                        }

                        retryNextUrl = retryJsonData.next_url;
                        if (retryNextUrl) {
                            retryCurrentUrl = this.wrapAbsoluteUrlToProxy(retryNextUrl);
                        }
                    } while (retryNextUrl && retryPageCount < maxPages);

                    if (retryResults.length === 0) {
                        console.error("❌ Still no data after retrying with last trading day");
                        return [];
                    } else {
                        allResults = retryResults;
                    }
                } else {
                    console.error("❌ No data available and no fallback options");
                    return [];
                }
            }

            const stockPoints = allResults.map(result => ({
                DateTime: new Date(result.t).toISOString(),
                Open: parseFloat(result.o || 0),
                High: parseFloat(result.h || 0),
                Low: parseFloat(result.l || 0),
                Close: parseFloat(result.c || 0),
                Volume: parseFloat(result.v || 0)
            }));

            stockPoints.sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
            return stockPoints;
        } catch (error) {
            console.error(`❌ Error fetching stock data for ${stockSymbol}: ${error.message}`);
            return [];
        }
    }

    // Parity: fetchStockSplits (alias-style)
    async fetchStockSplits(stockSymbol, startDate, endDate) {
        try {
            const url = this.buildPolygonUrl('/v3/reference/splits', {
                ticker: stockSymbol,
                'execution_date.gte': startDate,
                'execution_date.lte': endDate
            });
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const jsonData = await response.json();
            if (!jsonData.results || !Array.isArray(jsonData.results)) {
                console.warn(`❌ No splits data available or invalid 'results' field for ${stockSymbol}.`);
                return [];
            }
            return jsonData.results.map(result => ({
                execution_date: result.execution_date,
                split_from: result.split_from,
                split_to: result.split_to
            }));
        } catch (error) {
            console.error(`❌ Error fetching stock splits for ${stockSymbol}: ${error.message}`);
            return [];
        }
    }

    // Parity: fetchStockAndSplits
    async fetchStockAndSplits(stockSymbol, multiplier, timespan, startDate, endDate) {
        try {
            const [stockPoints, splits] = await Promise.all([
                this.fetchStockData(stockSymbol, multiplier, timespan, startDate, endDate),
                this.fetchStockSplits(stockSymbol, startDate, endDate)
            ]);
            return { stockPoints, splits };
        } catch (error) {
            console.error(`❌ Error fetching stock data and splits for ${stockSymbol}: ${error.message}`);
            return { stockPoints: [], splits: [] };
        }
    }
    // Parity: fetchStockDividendsData with pagination via proxy
    async fetchStockDividendsData(stockSymbol, startDate, endDate) {
        try {
            let currentUrl = this.buildPolygonUrl('/v3/reference/dividends', {
                ticker: stockSymbol,
                'ex_dividend_date.gte': startDate,
                'ex_dividend_date.lte': endDate,
                limit: 1000,
                sort: 'ex_dividend_date',
                order: 'desc'
            });

            let allDividends = [];
            let nextUrl = null;
            let pageCount = 0;
            const maxPages = 50; // Safety limit

            do {
                pageCount++;
                const response = await fetch(currentUrl);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const jsonData = await response.json();

                if (jsonData.results && Array.isArray(jsonData.results)) {
                    allDividends = allDividends.concat(jsonData.results);
                }

                nextUrl = jsonData.next_url;
                if (nextUrl) {
                    currentUrl = this.wrapAbsoluteUrlToProxy(nextUrl);
                }

                if (pageCount >= maxPages) {
                    console.warn(`⚠️ Reached maximum page limit (${maxPages}) for dividends, stopping pagination`);
                    break;
                }
            } while (nextUrl && pageCount < maxPages);

            return allDividends.map(d => ({
                ex_dividend_date: d.ex_dividend_date,
                cash_amount: d.cash_amount,
                declaration_date: d.declaration_date,
                pay_date: d.pay_date,
                record_date: d.record_date,
                dividend_type: d.dividend_type,
                frequency: d.frequency
            }));

        } catch (error) {
            console.error(`❌ Error fetching dividends for ${stockSymbol}: ${error.message}`);
            return [];
        }
    }

    // Parity: fetchStockShortInterestData with pagination via proxy
    async fetchStockShortInterestData(stockSymbol, startDate, endDate) {
        try {
            let currentUrl = this.buildPolygonUrl('/stocks/v1/short-interest', {
                ticker: stockSymbol,
                'settlement_date.gte': startDate,
                'settlement_date.lte': endDate,
                limit: 1000,
            });

            let allShortInterest = [];
            let nextUrl = null;
            let pageCount = 0;
            const maxPages = 50; // Safety limit

            do {
                pageCount++;
                const response = await fetch(currentUrl);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const jsonData = await response.json();

                if (jsonData.results && Array.isArray(jsonData.results)) {
                    allShortInterest = allShortInterest.concat(jsonData.results);
                }

                nextUrl = jsonData.next_url;
                if (nextUrl) {
                    currentUrl = this.wrapAbsoluteUrlToProxy(nextUrl);
                }

                if (pageCount >= maxPages) {
                    console.warn(`⚠️ Reached maximum page limit (${maxPages}) for short interest, stopping pagination`);
                    break;
                }
            } while (nextUrl && pageCount < maxPages);

            return allShortInterest.map(d => ({
                settlement_date: d.settlement_date,
                short_interest: d.short_interest,
                avg_daily_volume: d.avg_daily_volume,
                days_to_cover: d.days_to_cover,
            }));

        } catch (error) {
            console.error(`❌ Error fetching short interest for ${stockSymbol}: ${error.message}`);
            return [];
        }
    }

    // Parity: fetchStockNews via Polygon reference/news
    async fetchStockNews(stockSymbol, limit = 10, startDate = null, endDate = null) {
        try {
            const params = { ticker: stockSymbol, limit: String(limit), order: 'desc' };
            if (startDate) params['published_utc.gte'] = startDate;
            if (endDate) params['published_utc.lte'] = endDate;
            const url = this.buildPolygonUrl('/v2/reference/news', params);
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const jsonData = await response.json();
            if (!jsonData.results || !Array.isArray(jsonData.results)) {
                console.warn(`❌ No news data available or invalid 'results' field for ${stockSymbol}.`);
                return [];
            }
            return jsonData.results.map(article => ({
                title: article.title,
                publisher: article.publisher?.name,
                publishedDate: article.published_utc,
                url: article.article_url,
                description: article.description || '',
                tickers: article.tickers || []
            }));
        } catch (error) {
            console.error(`❌ Error fetching news for ${stockSymbol}: ${error.message}`);
            return [];
        }
    }

    // Parity: fetchTickerData (returns results only)
    async fetchTickerData(tickerSymbol) {
        const url = this.buildPolygonUrl(`/v3/reference/tickers/${encodeURIComponent(tickerSymbol)}`);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error for ${tickerSymbol}: ${response.status}`);
        const data = await response.json();
        return data.results || null;
    }
}

// Expose V2
window.PolygonAPIManagerV2 = PolygonAPIManagerV2;
// Compatibility alias to ease migration of existing callers expecting PolygonAPIManager
window.PolygonAPIManager = PolygonAPIManagerV2;
